import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { BaseFieldControlComponent } from '@d-components/base-field-control/base-field-control';
import { BaseLabelComponent } from '@d-components/label/label.component';
import { DEFAULT_FORMAT_DATE } from '@d-constants';
import { DateValidators } from 'app/core/validators/date-time.validators';

// to disable warnings then we have a config in angular.json allowedCommonJsDependencies
import dayjs from 'dayjs';
import { isEqual } from 'lodash-es';
import { CalendarModule, CalendarTypeView } from 'primeng/calendar';
import { combineLatest, startWith, Subject, takeUntil } from 'rxjs';

const customParseFormat = require('dayjs/plugin/customParseFormat');
dayjs.extend(customParseFormat);

@Component({
  selector: 'app-base-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CalendarModule,
    BaseLabelComponent,
  ],
})
export class BaseDatepickerComponent
  extends BaseFieldControlComponent
  implements OnInit, OnChanges
{
  @Input() dateFormat!: string;
  @Input() mode: 'range' | 'single' | 'range_2' = 'single';
  @Input() override placeholder: string = DEFAULT_FORMAT_DATE;
  @Input() minDate!: Date;
  @Input() maxDate!: Date;
  @Input() view?: CalendarTypeView = 'date';
  @Input() showTime: boolean = false;
  destroy$: Subject<boolean> = new Subject<boolean>();

  date_from = new FormControl();
  date_to = new FormControl();
  dateToErrorMessage = '';
  constructor() {
    super();
  }

  ngOnInit(): void {
    if (this.mode === 'range_2') {
      this.date_from.addValidators(this.invalidDateFormat(this.dateFormat));
      this.date_to.addValidators(this.invalidDateFormat(this.dateFormat));

      if (this.fieldControl.hasValidator(Validators.required)) {
        this.fieldControl.removeValidators(Validators.required);
        this.fieldControl.addValidators(this.customRequiredValidatorRange2);
        this.date_from.addValidators(Validators.required);
        this.date_to.addValidators(Validators.required);
      }

      const _this = this;

      const func = FormControl.prototype.markAsTouched;

      FormControl.prototype.markAsTouched = function () {
        func.call(this);
        if (Array.isArray(this.value)) {
          _this.date_from.setValue(_this.date_from.value);
          _this.date_to.setValue(_this.date_to.value);
          _this.date_from.markAsTouched();
          _this.date_to.markAsTouched();
        }
      };

      this.fieldControl.statusChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((status) => {
          if (status === 'DISABLED') {
            this.setDisabledRange(true);
          }
        });

      this.date_from.updateValueAndValidity();
      this.date_to.updateValueAndValidity();
      if (typeof this.fieldControl.value === 'string') {
        this.fieldControl.setValue([]);
        this.date_from.setValue(new Date());
        this.date_from.markAsTouched();
      } else {
        if (this.fieldControl.value) {
          if (
            (this.fieldControl.value[0] === '' &&
              this.fieldControl.value[1] === '') ||
            (this.fieldControl.value[0] === null &&
              this.fieldControl.value[1] === null)
          ) {
            this.date_from.reset(null, { emitEvent: false });
            this.date_to.reset(null, { emitEvent: false });
          } else {
            this.date_from.setValue(
              this.fieldControl.value &&
                this.fieldControl.value[0] instanceof Date
                ? this.fieldControl.value[0]
                : new Date(this.fieldControl.value?.[0]),
            );
            this.date_to.setValue(
              this.fieldControl.value &&
                this.fieldControl.value[1] instanceof Date
                ? this.fieldControl.value[1]
                : new Date(this.fieldControl.value?.[1]),
            );
          }
        }
      }

      combineLatest([
        this.date_from.valueChanges.pipe(startWith(this.date_from.value)),
        this.date_to.valueChanges.pipe(startWith(this.date_to.value)),
      ]).subscribe(([from, to]) => {
        if (from && to && dayjs(to).isBefore(dayjs(from))) {
          this.date_from.setErrors({ invalid_range: true });
          this.date_to.setErrors({ invalid_range: true });
          this.fieldControl.setErrors({ invalid_range: true });
        }

        if (
          from &&
          to &&
          !dayjs(to).isBefore(dayjs(from)) &&
          (this.date_from.hasError('invalid_range') ||
            this.date_to.hasError('invalid_range'))
        ) {
          this.date_from.setErrors({ invalid_range: null });
          this.date_to.setErrors({ invalid_range: null });
          this.date_from.updateValueAndValidity();
          this.date_to.updateValueAndValidity();
        }
        if (!isEqual(this.fieldControl.value, [from, to])) {
          this.fieldControl.setValue([from, to]);
        }
      });

      this.fieldControl.valueChanges.subscribe((val) => {
        if (
          val === null ||
          val === '' ||
          (Array.isArray(val) && val.every((e) => e === null || e === ''))
        ) {
          if (this.mode === 'range_2') {
            this.date_from.reset();
            this.date_to.reset();
          } else {
            this.date_from.reset();
          }
        } else {
          if (val[0] !== this.date_from.value) {
            this.date_from.setValue(
              val[0] instanceof Date ? val[0] : new Date(val[0]),
            );
            this.date_from.markAsTouched();
          }

          if (val[1] !== this.date_to.value) {
            this.date_to.setValue(
              val[0] instanceof Date ? val[1] : new Date(val[1]),
            );
            this.date_to.markAsTouched();
          }
        }
      });

      this.setDisabledRange(this.disabled);
    } else {
      // this.fieldControl.addValidators(this.invalidDateFormat(this.dateFormat));
      let debug = this.fieldControl.value;
      let validators = [this.invalidDateFormat(this.dateFormat)];
      if (this.required) {
        validators = [Validators.required, ...validators];
      }
      this.fieldControl.addValidators(validators);
      this.fieldControl.updateValueAndValidity();
    }

    this.errorMessageList = {
      format: 'The format is invalid.',
      invalid_range: 'From Date must before To Date',
      ...this.errorMessageList,
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.mode === 'range_2' && changes.disabled) {
      this.setDisabledRange(changes.disabled.currentValue);
    }
  }

  setDisabledRange(disabled: boolean) {
    if (disabled) {
      this.date_from.disable();
      this.date_to.disable();
    } else {
      this.date_from.enable();
      this.date_to.enable();
    }
  }

  invalidDateFormat(format: string = DEFAULT_FORMAT_DATE): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (typeof control.value === 'string') {
        return !control.value || dayjs(control.value, format).isValid()
          ? null
          : { format: true };
      } else {
        return !control.value || dayjs(control.value).isValid()
          ? null
          : { format: true };
      }
    };
  }

  checkInvalidField(): boolean {
    if (this.mode !== 'range_2') return super.checkInvalidField();
    const invalid =
      (this.date_from?.dirty || this.date_from?.touched) &&
      this.date_from?.invalid;
    if (invalid && !this.disabled) {
      this.errorMessage = !!this.errorMessageList
        ? this.errorMessageList[Object.keys(this.date_from.errors || [])[0]]
        : '';
    }
    return invalid;
  }

  checkInvalidDateToField(): boolean {
    const invalid =
      (this.date_to?.dirty || this.date_to?.touched) && this.date_to?.invalid;
    if (invalid && !this.disabled) {
      this.errorMessage = !!this.errorMessageList
        ? this.errorMessageList[Object.keys(this.date_to.errors || [])[0]]
        : '';
      // not show invalid_range error message in date_to
      this.dateToErrorMessage = !!this.errorMessageList
        ? this.errorMessageList[
            Object.keys(this.date_to.errors || []).filter(
              (e) => e !== 'invalid_range',
            )[0]
          ]
        : '';
    }
    return invalid;
  }

  customRequiredValidatorRange2 = (control: AbstractControl) => {
    if (!control.value || (!control.value[0] && !control.value[1]))
      return { required: true };
    return null;
  };

  IsArrayLabel(label: any) {
    return Array.isArray(label) && label.length > 1;
  }
}
